import { SelectMenu, Typography } from '@scale/scaleui';
import { memo, useCallback, useMemo } from 'react';
import {
  InteractionStepType,
  TextCollectionPerResponseStepParams,
  UIGenerationEnforcementMode,
} from '@scale/shared/types/chat';
import FieldsBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/FieldsBuilder';
import StringInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/StringInputBuilder';
import BooleanInputBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/BooleanInputBuilder';
import { TextCollectionField } from '@scale/shared/types/params/textcollection';
import OverrideIDBuilder from 'react-scale/corp/components/QM/taskTemplatesEditor/OverrideIDBuilder';
import {
  EnableRTLTextConfig,
  ConditionalStepConfig,
  ReviewLevelConfig,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/definitions/configs';
import { StepConfigurationComponentProps } from '../../stepDescriptor/types';
import { ExperimentalFeature } from 'react-scale/components/scalers/tasks/ThreadedInteraction/ChatConfigurationEditor/components/ConfigLifeCycleInfo';
import { Select } from '@scale/scaleui-radix';
import { typeBuilderMap } from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderSchemas';

type TFieldIdOption = { id: string; name: string };

const TextCollectionPerResponseConfigurationComponent = ({
  params: currentParams,
  setParams,
  previousParams,
  taskParams,
  deliveryBuilderConfig,
}: StepConfigurationComponentProps<InteractionStepType.TextCollectionPerResponse>) => {
  const setCurrentParams = useCallback(
    (newParams: TextCollectionPerResponseStepParams) => {
      setParams(newParams);
    },
    [setParams],
  );

  const fieldIds = useMemo<TFieldIdOption[]>(() => {
    return currentParams.params.fields.map((e: { field_id: string }) => ({
      id: e.field_id,
      name: e.field_id,
    }));
  }, [currentParams]);

  return (
    <div className="flex flex-col gap-4">
      <StringInputBuilder
        name={'Title'}
        existingValue={currentParams.title}
        updateProperty={(value: string) => setCurrentParams({ ...currentParams, title: value })}
        required={true}
      />

      <OverrideIDBuilder
        params={currentParams}
        previousParams={previousParams}
        setParams={setCurrentParams}
      />

      <StringInputBuilder
        name={'Subtitle'}
        existingValue={currentParams.subtitle}
        updateProperty={(value: string) => setCurrentParams({ ...currentParams, subtitle: value })}
      />

      <div className="flex flex-col gap-2">
        <SelectMenu
          label="Ensure Ranking/Selection Consistency for the Given Field ID"
          options={[
            {
              id: '',
              name: 'Select field',
            },
            ...fieldIds,
          ]}
          selectedOptionId={currentParams.params.fieldToCheck || ''}
          setSelected={(value: string) => {
            const newValue = value || undefined;
            setCurrentParams({
              ...currentParams,
              params: { ...currentParams.params, fieldToCheck: newValue },
            });
          }}
        />
        <div className="prose text-sm italic">
          When a Field ID is provided, taskers will be forced to ensure that their ratings agree
          with their rankings or model selection choices, i.e. higher rated responses are either
          higher in rank or chosen as the best response.
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <div className="font-medium">Fields</div>
        <div className="text-sm text-neutral-500">
          Add fields to collect from the user. You can add as many fields as you want.
        </div>
      </div>
      <FieldsBuilder
        typeBuilderMap={typeBuilderMap}
        name={''}
        stepId={currentParams.id}
        existingValue={currentParams.params.fields}
        updateProperty={(value: TextCollectionField[]) => {
          // Need to check if we deleted the fieldToCheck as well
          const fieldToCheck =
            currentParams.params.fieldToCheck &&
            !value.map(field => field.field_id).includes(currentParams.params.fieldToCheck)
              ? undefined
              : currentParams.params.fieldToCheck;
          setCurrentParams({
            ...currentParams,
            params: { ...currentParams.params, fields: value, fieldToCheck },
          });
        }}
        taskTemplateParams={taskParams}
        deliveryBuilderConfig={deliveryBuilderConfig}
      />

      <div className="flex flex-col gap-2">
        <BooleanInputBuilder
          name={'Enable Inline Comments'}
          existingValue={!!currentParams.params.enable_inline_comments}
          updateProperty={(value: boolean) =>
            setCurrentParams({
              ...currentParams,
              params: { ...currentParams.params, enable_inline_comments: value },
            })
          }
        />
        <div className="prose text-sm italic ml-8">
          If enabled, then users will have the ability to highlight and leave comments on each
          response.
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <EnableRTLTextConfig
          value={currentParams.params.enable_rtl_text}
          onChange={value => {
            setCurrentParams({
              ...currentParams,
              params: { ...currentParams.params, enable_rtl_text: value },
            });
          }}
        />
      </div>

      <ExperimentalFeature
        name="Enable UI generation"
        details="If enabled, allows contributors to render a UI of the code"
        showInfoCondition={!!currentParams.params.enable_ui_generation}
      >
        <div className="flex flex-col gap-2">
          <BooleanInputBuilder
            name="Enable UI generation"
            existingValue={!!currentParams.params.enable_ui_generation}
            updateProperty={(value: boolean) =>
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, enable_ui_generation: value },
              })
            }
          />
          <div className="prose text-sm italic ml-8">
            If enabled, allows contributors to render a UI of the code
          </div>
        </div>
      </ExperimentalFeature>
      {!!currentParams.params.enable_ui_generation && (
        <div className="flex flex-col gap-2">
          <Typography.Subtitle2>UI generation enforcement mode</Typography.Subtitle2>
          <Select.Root
            size="2"
            value={
              currentParams.params.ui_generation_enforcement_mode ??
              UIGenerationEnforcementMode.None
            }
            onValueChange={(value: string) =>
              setCurrentParams({
                ...currentParams,
                params: {
                  ...currentParams.params,
                  ui_generation_enforcement_mode: value as UIGenerationEnforcementMode,
                },
              })
            }
          >
            <Select.Trigger placeholder="Select UI generation enforcement mode" />
            <Select.Content position="popper">
              <Select.Item value={UIGenerationEnforcementMode.None}>None</Select.Item>
              <Select.Item value={UIGenerationEnforcementMode.CompilationAttempted}>
                Must Attempt Compilation
              </Select.Item>
              <Select.Item value={UIGenerationEnforcementMode.CompilationSuccessful}>
                Must Compile Successfully
              </Select.Item>
            </Select.Content>
          </Select.Root>
        </div>
      )}

      <ExperimentalFeature
        name="Extract model response regex"
        details="If present, the model response will be extracted using the provided regex. This does not change the chat history"
        showInfoCondition={!!currentParams.params.extract_model_response_regex}
      >
        <div className="flex flex-col gap-2">
          <StringInputBuilder
            name="Extract model response regex"
            existingValue={currentParams.params.extract_model_response_regex || ''}
            updateProperty={(value: string) =>
              setCurrentParams({
                ...currentParams,
                params: { ...currentParams.params, extract_model_response_regex: value },
              })
            }
          />
          <div className="prose text-sm italic ml-8">
            If present, the model response will be extracted using the provided regex. This only
            affects the frontend display and not the chat history
          </div>
        </div>
      </ExperimentalFeature>

      <ReviewLevelConfig currentParams={currentParams} setCurrentParams={setCurrentParams} />
      <ConditionalStepConfig
        currentParams={currentParams}
        setCurrentParams={setCurrentParams}
        previousParams={previousParams}
        taskParams={taskParams}
      />
    </div>
  );
};

export default memo(TextCollectionPerResponseConfigurationComponent);
